import { Link } from 'react-router-dom';

import { useCollections } from '@/features/collections/useCollections';
import { MessageAccessPrompt } from '@/features/notifications/MessageAccessPrompt';
import { useReferralSummary } from '@/features/referrals/useReferralSummary';
import { describeSelections } from '@/utils/selections';

import { DownloadButton } from './actions/DownloadButton';
import { FavoriteToggleButton } from './actions/FavoriteToggleButton';
import { ShareButton } from './actions/ShareButton';
import { useGenerationResultQuery } from './useGenerationResultQuery';

interface ResultScreenProps {
  generationId: string;
}

export function ResultScreen({ generationId }: ResultScreenProps) {
  const { data, isPending, isError, refetch } = useGenerationResultQuery(generationId);
  const { byId } = useCollections();
  const referral = useReferralSummary();

  if (isPending) {
    return <p className="px-6 py-8 text-text-2">Загрузка результата…</p>;
  }

  if (isError) {
    return (
      <div className="px-6 py-8">
        <p className="text-danger">Не удалось загрузить результат. Попробуйте ещё раз.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-sm text-accent hover:underline"
        >
          Повторить
        </button>
      </div>
    );
  }

  // Не различаем «не найдено» и «чужое» — см. services/generationResult.ts.
  if (!data) {
    return <p className="px-6 py-8 text-text-2">Генерация не найдена.</p>;
  }

  const collection = byId.get(data.collectionId);
  const collectionTitle = collection?.title ?? data.collectionId;
  const createHref = `/create/${collection?.slug ?? data.collectionId}`;

  return (
    <div className="flex flex-col gap-6 px-6 py-8">
      <div>
        <p className="text-sm text-text-3">{collectionTitle}</p>
        <p className="text-xs text-text-3">{describeSelections(collection, data.selections)}</p>
      </div>

      {/*
        Пока подключён mock-провайдер, результат — не настоящая генерация,
        и об этом говорится прямо: обещать то, чего не было, нельзя.
      */}
      {data.provider === 'mock' && (
        <p className="rounded-card border border-border bg-surface px-3 py-2 text-xs text-text-2">
          Демонстрационный режим: реальная генерация не подключена, изображение
          сделано заглушкой.
        </p>
      )}

      {data.images.length === 0 && <p className="text-text-2">Изображений пока нет.</p>}

      {data.images.map((image) => (
        <div key={image.id} className="flex flex-col gap-3">
          {image.signedUrl ? (
            <img
              src={image.signedUrl}
              alt={`Портрет — ${collectionTitle}`}
              className="w-full rounded-card object-cover"
            />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center rounded-card bg-surface-2 text-text-3">
              Изображение недоступно
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <DownloadButton path={image.path} />
            {/*
              Делимся ссылкой на приложение, а не подписанной ссылкой на
              картинку: последняя истекает через час (см. ShareButton).
            */}
            <ShareButton collectionTitle={collectionTitle} referralCode={referral.data?.code} />
            <FavoriteToggleButton
              generationImageId={image.id}
              favoriteId={image.favoriteId}
              isFavorite={image.isFavorite}
            />
          </div>
        </div>
      ))}

      <MessageAccessPrompt />

      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        {/*
          «Повторить» переносит в мастер те же параметры, но не снимки:
          сервер исходные фотографии не хранит (и хранить их — отдельный
          риск), поэтому фото пользователь выбирает заново.
        */}
        <Link
          to={createHref}
          state={{ preset: { groups: data.selections, aspectRatioId: data.aspectRatioId } }}
          className="btn btn-secondary px-4 py-2 text-sm"
        >
          Повторить
        </Link>

        <Link
          to={createHref}
          className="btn btn-secondary px-4 py-2 text-sm"
        >
          Создать ещё
        </Link>

        {/* Размер серии выбирается в мастере — здесь развилки на 5 и 10 не нужно. */}
        <Link
          to={createHref}
          className="btn btn-secondary px-4 py-2 text-sm"
        >
          Создать фотосессию
        </Link>
      </div>
    </div>
  );
}
